export enum AnchoredPosition {
  TOP_LEFT,
  TOP_CENTER,
  TOP_RIGHT,
  CENTER_RIGHT,
  BOTTOM_RIGHT,
  BOTTOM_CENTER,
  BOTTOM_LEFT,
  CENTER_LEFT,
}

export const ANCHORED_POSITIONS = [
  AnchoredPosition.TOP_LEFT,
  AnchoredPosition.TOP_CENTER,
  AnchoredPosition.TOP_RIGHT,
  AnchoredPosition.CENTER_RIGHT,
  AnchoredPosition.BOTTOM_RIGHT,
  AnchoredPosition.BOTTOM_CENTER,
  AnchoredPosition.BOTTOM_LEFT,
  AnchoredPosition.CENTER_LEFT,
];

type Dimensions = {
  width: number;
  height: number;
};

type Point = {
  x: number;
  y: number;
};

export type Box = Point & Dimensions;

export type Anchor = Point & { position: AnchoredPosition };

export function getOrigin(
  anchor: Box,
  client: Dimensions,
  viewportSize: Dimensions,
  preferredPosition: AnchoredPosition,
  allowedPositions: AnchoredPosition[] | null = null,
  fallbackDirection: 'cw' | 'ccw' = 'ccw',
): Anchor {
  let position = preferredPosition;
  const allowed =
    !allowedPositions || allowedPositions.length === 0
      ? ANCHORED_POSITIONS
      : allowedPositions;
  let index = allowed.indexOf(position);
  let leastClippedIndex = index;
  let origin = originAtPosition(anchor, client, position);
  let currentClippedX;
  let currentClippedY;
  let leastClipped = Infinity;
  let foundUnclipped = false;
  // Iterate through allowed positions. The first one we find that doesn't clip out of view, we use.
  // If all of the positions clip, use the one which clipped the least.
  for (
    let checkCount = 0;
    checkCount <= allowed.length && !foundUnclipped;
    checkCount++,
      index =
        fallbackDirection === 'cw'
          ? (index + 1) % allowed.length
          : (index - 1 + allowed.length) % allowed.length
  ) {
    position = allowed[index];
    origin = originAtPosition(anchor, client, position);
    currentClippedX =
      -1 *
      Math.min(origin.x, viewportSize.width - (origin.x + client.width), 0);
    currentClippedY =
      -1 *
      Math.min(origin.y, viewportSize.height - (origin.y + client.height), 0);
    if (currentClippedX > 0 || currentClippedY > 0) {
      if (leastClipped > currentClippedX + currentClippedY) {
        leastClipped = currentClippedX + currentClippedY;
        leastClippedIndex = index;
      }
    } else {
      foundUnclipped = true;
    }
  }
  if (!foundUnclipped) {
    position = allowed[leastClippedIndex];
    origin = originAtPosition(anchor, client, position);
    origin.y = origin.y < 0 ? 4 : origin.y;
  }
  return { ...origin, position };
}

export function originAtPosition(
  anchor: Box,
  client: Dimensions,
  position: AnchoredPosition,
): Point {
  switch (position) {
    case AnchoredPosition.TOP_LEFT:
      return {
        x: anchor.x + anchor.width - client.width,
        y: anchor.y - client.height,
      };
    case AnchoredPosition.TOP_CENTER:
      return {
        x: anchor.x + anchor.width / 2 - client.width / 2,
        y: anchor.y - client.height,
      };
    case AnchoredPosition.TOP_RIGHT:
      return {
        x: anchor.x,
        y: anchor.y - client.height,
      };
    case AnchoredPosition.CENTER_RIGHT:
      return {
        x: anchor.x + anchor.width,
        y: anchor.y + anchor.height / 2 - client.height / 2,
      };
    case AnchoredPosition.BOTTOM_RIGHT:
      return {
        x: anchor.x,
        y: anchor.y + anchor.height,
      };
    case AnchoredPosition.BOTTOM_CENTER:
      return {
        x: anchor.x + anchor.width / 2 - client.width / 2,
        y: anchor.y + anchor.height,
      };
    case AnchoredPosition.BOTTOM_LEFT:
      return {
        x: anchor.x + anchor.width - client.width,
        y: anchor.y + anchor.height,
      };
    case AnchoredPosition.CENTER_LEFT:
      return {
        x: anchor.x - client.width,
        y: anchor.y + anchor.height / 2 - client.height / 2,
      };
  }
}
